import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'large' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'large') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block relative rounded-xl overflow-hidden cursor-pointer h-[400px] md:h-[460px]"
      >
        <img
          src={article.imageUrl}
          alt={article.title}
          title={article.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>

        {article.isHot && (
          <span className="absolute top-4 right-4 px-3 py-1 bg-accent-500 text-background-50 text-xs font-bold rounded-full whitespace-nowrap">
            爆
          </span>
        )}

        <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
          <span className="inline-block px-2.5 py-1 bg-primary-500/90 text-background-50 text-xs rounded-md mb-3 whitespace-nowrap">
            {article.category}
          </span>
          <h3 className="text-white text-lg md:text-xl font-bold leading-snug mb-2 group-hover:text-primary-300 transition-colors">
            {article.title}
          </h3>
          <p className="text-white/70 text-sm line-clamp-2">{article.excerpt}</p>
          <div className="flex items-center gap-4 mt-3 text-white/50 text-xs">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.readCount >= 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-heart-line"></i>
              {article.likeCount >= 10000 ? `${(article.likeCount / 10000).toFixed(1)}万` : article.likeCount}
            </span>
            <span>{article.publishDate}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-24 h-24 md:w-28 md:h-28 rounded-lg overflow-hidden flex-shrink-0">
          <img
            src={article.imageUrl}
            alt={article.title}
            title={article.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-foreground-900 text-sm font-medium line-clamp-2 mb-1 group-hover:text-primary-500 transition-colors">
            {article.title}
          </h4>
          <p className="text-foreground-500 text-xs line-clamp-2 mb-2">{article.excerpt}</p>
          <div className="flex items-center gap-3 text-foreground-400 text-xs">
            <span className="text-primary-500 font-medium whitespace-nowrap">{article.category}</span>
            <span>{article.publishDate}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200 hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="relative h-48 md:h-52 overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          title={article.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />

        {article.isHot && (
          <span className="absolute top-3 right-3 px-2.5 py-0.5 bg-accent-500 text-background-50 text-xs font-bold rounded-full whitespace-nowrap">
            爆
          </span>
        )}
        {article.isTrending && !article.isHot && (
          <span className="absolute top-3 right-3 px-2.5 py-0.5 bg-primary-500 text-background-50 text-xs font-bold rounded-full whitespace-nowrap">
            热
          </span>
        )}
      </div>

      <div className="p-4 md:p-5">
        <span className="inline-block text-xs text-primary-500 font-medium mb-2 whitespace-nowrap">
          {article.category}
        </span>
        <h3 className="text-foreground-900 text-base font-bold leading-snug mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
          {article.title}
        </h3>
        <p className="text-foreground-500 text-sm line-clamp-2 mb-3">{article.excerpt}</p>
        <div className="flex items-center justify-between text-foreground-400 text-xs">
          <span>{article.publishDate}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {article.readCount >= 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}
          </span>
        </div>
      </div>
    </Link>
  );
}